import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidatePortalAPI } from '../services/api';
import './JobList.css';

// ── Solitaire Overseas — Brand Logo ──────────────────────────
const SolitaireLogo = ({ size = 64 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Solitaire Overseas logo">
    <defs>
      <linearGradient id="so-bg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
        <stop stopColor="#0f1b4c"/>
        <stop offset="1" stopColor="#1e2d7a"/>
      </linearGradient>
      <linearGradient id="so-crown" x1="32" y1="8" x2="32" y2="28" gradientUnits="userSpaceOnUse">
        <stop stopColor="#fff8d4"/>
        <stop offset="1" stopColor="#d4af37"/>
      </linearGradient>
      <linearGradient id="so-pav-l" x1="14" y1="28" x2="32" y2="56" gradientUnits="userSpaceOnUse">
        <stop stopColor="#b8940e"/>
        <stop offset="1" stopColor="#7a5f00"/>
      </linearGradient>
      <linearGradient id="so-pav-r" x1="50" y1="28" x2="32" y2="56" gradientUnits="userSpaceOnUse">
        <stop stopColor="#e8c43c"/>
        <stop offset="1" stopColor="#b89010"/>
      </linearGradient>
      <linearGradient id="so-pav-c" x1="32" y1="28" x2="32" y2="56" gradientUnits="userSpaceOnUse">
        <stop stopColor="#d4af37"/>
        <stop offset="1" stopColor="#9a7208"/>
      </linearGradient>
    </defs>
    {/* Rounded background */}
    <rect width="64" height="64" rx="16" fill="url(#so-bg)"/>
    {/* Subtle glow ring */}
    <circle cx="32" cy="32" r="26" stroke="rgba(212,175,55,0.15)" strokeWidth="1"/>

    {/* === DIAMOND === */}
    {/* Crown: top center triangle (table) */}
    <polygon points="32,9 44,26 20,26" fill="url(#so-crown)"/>
    {/* Crown: left bezel */}
    <polygon points="14,30 20,26 32,28" fill="#c9a010" opacity="0.9"/>
    {/* Crown: right bezel */}
    <polygon points="50,30 44,26 32,28" fill="#f0d055" opacity="0.9"/>
    {/* Crown table (flat top facet) */}
    <polygon points="20,26 44,26 32,28" fill="#fff3a0" opacity="0.85"/>
    {/* Girdle */}
    <line x1="14" y1="30" x2="50" y2="30" stroke="rgba(255,255,255,0.5)" strokeWidth="0.8"/>
    {/* Pavilion: left half */}
    <polygon points="14,30 32,28 32,55" fill="url(#so-pav-l)"/>
    {/* Pavilion: right half */}
    <polygon points="50,30 32,28 32,55" fill="url(#so-pav-r)"/>
    {/* Pavilion: inner table reflection */}
    <polygon points="20,30 44,30 32,28" fill="url(#so-pav-c)" opacity="0.7"/>
    {/* Highlight lines */}
    <line x1="32" y1="9" x2="14" y2="30" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
    <line x1="32" y1="9" x2="50" y2="30" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
    <line x1="32" y1="28" x2="32" y2="55" stroke="rgba(255,255,255,0.12)" strokeWidth="0.5"/>
    {/* Top sparkle */}
    <circle cx="32" cy="9" r="1.5" fill="#fff8d4" opacity="0.9"/>
  </svg>
);

const JobList = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [expandedJob, setExpandedJob] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const navigate = useNavigate();

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await candidatePortalAPI.getActiveJobs();
      
      let jobsData = [];
      
      if (response.data.success && Array.isArray(response.data.data)) {
        jobsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        jobsData = response.data;
      }
      
      setJobs(jobsData);
      
    } catch (error) {
      console.error('Error loading jobs:', error);
      setError('Failed to load job listings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  // Filter and sort jobs
  const filteredAndSortedJobs = jobs
    .filter(job => {
      const matchesSearch = job.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.skillsRequired?.some(skill => 
                           skill.toLowerCase().includes(searchTerm.toLowerCase())
                         );
      
      const matchesDepartment = departmentFilter === 'all' || job.department === departmentFilter;
      const matchesLocation = locationFilter === 'all' || job.location === locationFilter;
      const matchesType = typeFilter === 'all' || job.employmentType === typeFilter;
      
      return matchesSearch && matchesDepartment && matchesLocation && matchesType;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt || b.applicationDeadline) - new Date(a.createdAt || a.applicationDeadline);
        case 'deadline':
          return new Date(a.applicationDeadline) - new Date(b.applicationDeadline);
        case 'title':
          return a.jobTitle.localeCompare(b.jobTitle);
        case 'department':
          return a.department.localeCompare(b.department);
        default:
          return 0;
      }
    });

  const departments = [...new Set(jobs.map(job => job.department).filter(Boolean))];
  const locations = [...new Set(jobs.map(job => job.location).filter(Boolean))];
  const employmentTypes = [...new Set(jobs.map(job => job.employmentType).filter(Boolean))];

  const toggleJobExpansion = (jobId) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const handleViewDetails = (job) => {
    navigate(`/jobs/${job._id}`);
  };

  const handleQuickApply = (job) => {
    navigate(`/jobs/${job._id}/apply`);
  };

  const getJobStatus = (job) => {
    const now = new Date();
    const deadline = new Date(job.applicationDeadline);
    const timeDiff = deadline - now;
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    if (daysDiff < 0) return { status: 'expired', text: 'Expired', class: 'expired' };
    if (daysDiff <= 3) return { status: 'urgent', text: 'Closing Soon', class: 'urgent' };
    if (daysDiff <= 7) return { status: 'ending', text: 'Ending Soon', class: 'ending' };
    return { status: 'active', text: 'Active', class: 'active' };
  };

  const [copyMsg, setCopyMsg] = useState('');
  const copyJobLink = (job) => {
    const jobUrl = `${window.location.origin}/jobs/${job._id}`;
    navigator.clipboard.writeText(jobUrl).then(() => {
      setCopyMsg('Link copied!');
      setTimeout(() => setCopyMsg(''), 2000);
    });
  };

  if (loading) {
    return (
      <div className="career-page">
        <div className="career-loading">
          <div className="loading-spinner"></div>
          <h2>Loading Career Opportunities</h2>
          <p>Discover your next career move with Solitaire Overseas</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="career-page">
        <div className="career-error">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load Jobs</h3>
          <p>{error}</p>
          <button onClick={loadJobs} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="career-page">
      {copyMsg && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: '#28a745', color: '#fff', padding: '10px 20px',
          borderRadius: 10, fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          animation: 'fadeIn 0.3s ease',
        }}>{copyMsg}</div>
      )}
      {/* Hero Section */}
      <section className="career-hero">
        {/* Top nav bar */}
        <div className="hero-topbar">
          <div className="hero-topbar-inner">
            <div className="company-logo-row">
              <SolitaireLogo size={52} />
              <div className="company-name-block">
                <span className="company-name-main">Solitaire Overseas</span>
                <span className="company-name-sub">Private Limited</span>
              </div>
            </div>
            <div className="hero-stats">
              <a href="#jobs" className="stat-card stat-card-link" style={{ textDecoration: 'none' }}
                onClick={() => { setSearchTerm(''); setDepartmentFilter('all'); }}>
                <div className="stat-number">{jobs.length}</div>
                <div className="stat-label">Open Positions</div>
              </a>
              <a href="#jobs" className="stat-card stat-card-link" style={{ textDecoration: 'none' }}
                onClick={() => { setSearchTerm(''); setDepartmentFilter('all'); }}>
                <div className="stat-number">
                  {jobs.filter(job => getJobStatus(job).status !== 'expired').length}
                </div>
                <div className="stat-label">Active Roles</div>
              </a>
              <div className="stat-card" style={{ cursor: 'default' }}>
                <div className="stat-number">{departments.length || '5+'}</div>
                <div className="stat-label">Departments</div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero main content */}
        <div className="hero-main">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            We're Hiring
          </div>
          <h1 className="hero-headline">
            Build Your Career<br />
            <span className="hero-headline-accent">Across the Globe</span>
          </h1>
          <p className="hero-description">
            Join Solitaire Overseas and be part of a team that's shaping global opportunities.
            We believe in talent without borders.
          </p>
          <div className="hero-actions">
            <a href="#jobs" className="hero-cta-btn">
              View Open Positions
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </a>
            <div className="hero-trust-badges">
              <span className="trust-badge">🌍 Global Teams</span>
              <span className="trust-badge">📈 Fast Growth</span>
              <span className="trust-badge">🤝 Great Culture</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats Bar */}
      <div className="quick-stats-bar">
        <div className="stats-container">
          <div className="quick-stat">
            <span className="stat-icon">🌍</span>
            <span>Global Opportunities</span>
          </div>
          <div className="quick-stat">
            <span className="stat-icon">💼</span>
            <span>Professional Growth</span>
          </div>
          <div className="quick-stat">
            <span className="stat-icon">🤝</span>
            <span>Supportive Environment</span>
          </div>
          <div className="quick-stat">
            <span className="stat-icon">⚡</span>
            <span>Quick Application</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="career-main" id="jobs">
        {/* Search and Filters Section */}
        <section className="search-filters-section">
          <div className="section-header">
            <h2>Find Your Perfect Role</h2>
            <p>Browse through our current openings and take the next step in your career</p>
          </div>

          <div className="search-container">
            <div className="search-box">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search by job title, department, or skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  ✕
                </button>
              )}
            </div>

            <button 
              className="mobile-filter-toggle"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span>🎛️</span>
              Filters
            </button>
          </div>

          {/* Filters Panel */}
          <div className={`filters-panel ${showMobileFilters ? 'active' : ''}`}>
            <div className="filter-group">
              <div className="filter-item">
                <label>Department</label>
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Departments</option>
                  {departments.map((dept, index) => (
                    <option key={dept || `dept-${index}`} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <label>Location</label>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Locations</option>
                  {locations.map((location, index) => (
                    <option key={location || `loc-${index}`} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <label>Job Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Job Types</option>
                  {employmentTypes.map((type, index) => (
                    <option key={type || `type-${index}`} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-item">
                <label>Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="filter-select"
                >
                  <option value="newest">Newest First</option>
                  <option value="deadline">Application Deadline</option>
                  <option value="title">Job Title A-Z</option>
                  <option value="department">Department</option>
                </select>
              </div>
            </div>

            {(searchTerm || departmentFilter !== 'all' || locationFilter !== 'all' || typeFilter !== 'all') && (
              <div className="filter-actions">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setDepartmentFilter('all');
                    setLocationFilter('all');
                    setTypeFilter('all');
                  }}
                  className="btn-clear-filters"
                >
                  🗑️ Clear All Filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Jobs Listing Section */}
        <section className="jobs-listing-section">
          <div className="jobs-header">
            <h3>
              Available Positions 
              <span className="jobs-count">({filteredAndSortedJobs.length})</span>
            </h3>
            <div className="view-toggle">
              <button className="view-btn active" style={{ pointerEvents: 'none' }}>📋 List View</button>
              <button className="view-btn" onClick={loadJobs} title="Refresh job listings">🔄 Refresh</button>
            </div>
          </div>

          {/* Jobs List - Mobile Cards & Desktop Table */}
          <div className="jobs-container">
            {/* Mobile View - Cards */}
            <div className="jobs-cards-mobile">
              {filteredAndSortedJobs.map((job) => {
                const jobStatus = getJobStatus(job);
                return (
                  <div key={job._id} className="job-card-mobile">
                    <div className="card-header">
                      <h4 className="job-title">{job.jobTitle}</h4>
                      <span className={`status-badge ${jobStatus.class}`}>
                        {jobStatus.text}
                      </span>
                    </div>
                    
                    <div className="card-meta">
                      <span className="meta-item">
                        <span className="meta-icon">🏢</span>
                        {job.department}
                      </span>
                      <span className="meta-item">
                        <span className="meta-icon">📍</span>
                        {job.location}
                      </span>
                      <span className="meta-item">
                        <span className="meta-icon">⏰</span>
                        {job.employmentType}
                      </span>
                    </div>

                    {job.description && (
                      <p className="job-description">
                        {job.description.length > 140
                          ? `${job.description.substring(0, 140).trim()}…`
                          : job.description}
                      </p>
                    )}

                    {job.skillsRequired && job.skillsRequired.length > 0 && (
                      <div className="skills-preview">
                        {job.skillsRequired.slice(0, 3).map((skill, index) => (
                          <span key={index} className="skill-tag">{skill}</span>
                        ))}
                        {job.skillsRequired.length > 3 && (
                          <span className="more-skills">+{job.skillsRequired.length - 3} more</span>
                        )}
                      </div>
                    )}

                    <div className="deadline-info">
                      <span className="deadline-text">
                        Apply by: {new Date(job.applicationDeadline).toLocaleDateString()}
                      </span>
                      <span className="days-left">
                        {Math.ceil((new Date(job.applicationDeadline) - new Date()) / (1000 * 60 * 60 * 24))} days left
                      </span>
                    </div>

                    <div className="card-actions">
                      <button
                        onClick={() => copyJobLink(job)}
                        className="btn-action btn-share"
                        title="Share Job"
                      >
                        📤 Share
                      </button>
                      <button
                        onClick={() => handleViewDetails(job)}
                        className="btn-action btn-view"
                      >
                        👁️ View
                      </button>
                      <button
                        onClick={() => handleQuickApply(job)}
                        className="btn-action btn-apply"
                        disabled={jobStatus.status === 'expired'}
                      >
                        {jobStatus.status === 'expired' ? 'Closed' : 'Apply'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop View - Table */}
            <div className="jobs-table-desktop">
              <table className="jobs-table">
                <thead>
                  <tr>
                    <th className="position-col">Position</th>
                    <th className="department-col">Department</th>
                    <th className="location-col">Location</th>
                    <th className="type-col">Type</th>
                    <th className="deadline-col">Deadline</th>
                    <th className="status-col">Status</th>
                    <th className="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedJobs.map((job) => {
                    const jobStatus = getJobStatus(job);
                    return (
                      <React.Fragment key={job._id}>
                        <tr className="job-row">
                          <td className="position-cell">
                            <div className="position-info">
                              <h4 className="job-title">{job.jobTitle}</h4>
                              {job.skillsRequired && job.skillsRequired.length > 0 && (
                                <div className="skills-preview">
                                  {job.skillsRequired.slice(0, 2).map((skill, index) => (
                                    <span key={index} className="skill-tag">{skill}</span>
                                  ))}
                                  {job.skillsRequired.length > 2 && (
                                    <span className="more-skills">+{job.skillsRequired.length - 2} more</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="department-cell">
                            <span className="department-badge">{job.department}</span>
                          </td>
                          <td className="location-cell">
                            <span className="location">📍 {job.location}</span>
                          </td>
                          <td className="type-cell">
                            <span className="employment-type">{job.employmentType}</span>
                          </td>
                          <td className="deadline-cell">
                            <div className="deadline-info">
                              <span className="date">{new Date(job.applicationDeadline).toLocaleDateString()}</span>
                              <span className="days-left">
                                {Math.ceil((new Date(job.applicationDeadline) - new Date()) / (1000 * 60 * 60 * 24))} days left
                              </span>
                            </div>
                          </td>
                          <td className="status-cell">
                            <span className={`status-badge ${jobStatus.class}`}>
                              {jobStatus.text}
                            </span>
                          </td>
                          <td className="actions-cell">
                            <div className="action-buttons">
                              <button
                                onClick={() => toggleJobExpansion(job._id)}
                                className="btn-action btn-view"
                                title="View Details"
                              >
                                {expandedJob === job._id ? '▲' : '▼'}
                              </button>
                              <button
                                onClick={() => copyJobLink(job)}
                                className="btn-action btn-share"
                                title="Share Job"
                              >
                                📤
                              </button>
                              <button
                                onClick={() => handleQuickApply(job)}
                                className="btn-action btn-apply"
                                disabled={jobStatus.status === 'expired'}
                                title={jobStatus.status === 'expired' ? 'Application Closed' : 'Apply Now'}
                              >
                                Apply
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded Row with Details */}
                        {expandedJob === job._id && (
                          <tr className="expanded-row">
                            <td colSpan="7">
                              <div className="job-details-expanded">
                                <div className="details-grid">
                                  <div className="detail-section">
                                    <h4>Job Description</h4>
                                    <p>{job.description}</p>
                                  </div>
                                  
                                  <div className="detail-section">
                                    <h4>Key Requirements</h4>
                                    <ul className="requirements-list">
                                      {job.requirements?.slice(0, 3).map((req, index) => (
                                        <li key={index}>{req}</li>
                                      ))}
                                    </ul>
                                  </div>
                                  
                                  <div className="detail-section">
                                    <h4>Skills Required</h4>
                                    <div className="skills-full">
                                      {job.skillsRequired?.map((skill, index) => (
                                        <span key={index} className="skill-tag">{skill}</span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="expanded-actions">
                                  <button
                                    onClick={() => handleViewDetails(job)}
                                    className="btn btn-outline"
                                  >
                                    View Full Details
                                  </button>
                                  <button
                                    onClick={() => handleQuickApply(job)}
                                    className="btn btn-primary"
                                    disabled={jobStatus.status === 'expired'}
                                  >
                                    {jobStatus.status === 'expired' ? 'Application Closed' : 'Apply for this Position'}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Empty State */}
          {filteredAndSortedJobs.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>No Jobs Found</h3>
              <p>
                {jobs.length === 0 
                  ? "We're currently not hiring, but please check back later for new opportunities at Solitaire Overseas."
                  : "No jobs match your current search criteria. Try adjusting your filters or search terms."
                }
              </p>
              {jobs.length > 0 && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setDepartmentFilter('all');
                    setLocationFilter('all');
                    setTypeFilter('all');
                  }}
                  className="btn btn-primary"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          )}
        </section>

        {/* Company Benefits Section */}
        <section className="benefits-section">
          <div className="section-header">
            <h2>Why Join Solitaire Overseas?</h2>
            <p>We offer a comprehensive benefits package and a supportive work environment</p>
          </div>
          
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🌍</div>
              <h4>Global Exposure</h4>
              <p>Work on international projects and expand your professional horizons</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">📈</div>
              <h4>Career Growth</h4>
              <p>Clear career progression paths and professional development opportunities</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">💼</div>
              <h4>Competitive Package</h4>
              <p>Attractive compensation and comprehensive benefits package</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">🤝</div>
              <h4>Supportive Culture</h4>
              <p>Collaborative work environment with strong team support</p>
            </div>
          </div>
        </section>

        {/* Share Section */}
        <section className="share-section">
          <div className="share-content">
            <h3>📤 Share Opportunities</h3>
            <p>Know someone who'd be perfect for a role? Share these opportunities with your network.</p>
            <div className="share-info">
              <p><strong>Each position has a unique URL:</strong></p>
              <code>https://solitaireoverseas.com/jobs/</code>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="career-footer">
        <div className="footer-content">
          <div>
            <div className="footer-logo-row">
              <SolitaireLogo size={40} />
              <div>
                <div className="footer-company-name">Solitaire Overseas</div>
                <div className="footer-company-sub">Private Limited</div>
              </div>
            </div>
            <p>Building global careers through innovation and excellence</p>
          </div>
          <div className="footer-links">
            <a href="#privacy">Privacy Policy</a>
            <a href="#terms">Terms of Service</a>
            <a href="#contact">Contact HR</a>
          </div>
        </div>
        <div className="footer-copyright">
          <p>&copy; {new Date().getFullYear()} Solitaire Overseas Private Limited. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default JobList;